import type { PreviewMode } from './playable-preview';

/**
 * A preview as a stack of sandboxed frames, so nothing ever reloads a frame in place (a blank flash): a new document
 * (an edited document, a size switch, Replay, a picker card's playthrough or loop restart) is mounted hidden over the
 * current one and fades in once it has loaded; the frame it covers is dropped only after the fade. Every preview
 * frame swaps its documents this way (one mode); a picker card on a runtime without the control channel also keeps
 * its resting loop running under a playthrough, so leaving fades the playthrough out over the loop where it is,
 * instead of restarting it. Pure (unit-tested in the node env); the layers render in key order, playthroughs above
 * the loop.
 */

/** The CSS pixel size a frame's document is laid out at. A frame keeps it (a size switch mounts a new frame), so the
 *  old document never re-lays out while the new one loads. */
export interface DemoFrameSize {
  w: number;
  h: number;
}

/** One frame of a preview. */
export interface DemoLayer {
  /** Stable identity (the frame's React key; a new key is a fresh document). Grows with each mount. */
  key: number;
  /** The resting loop or the hover playthrough (a plain preview frame has only `idle` layers). */
  mode: PreviewMode;
  html: string;
  /** The size it is laid out at (absent = the preview's current size). */
  size?: DemoFrameSize;
  /** When its document finished loading (ms); null while it loads (kept hidden). */
  shownAt: number | null;
  /** When it started fading out (ms); null while it is wanted. */
  leftAt: number | null;
}

export interface DemoStack {
  layers: DemoLayer[];
  nextKey: number;
}

export type DemoEvent =
  /** A fresh document of this mode is wanted (the loop, a playthrough, a restart or an edit). */
  | { type: 'mount'; mode: PreviewMode; html: string; size?: DemoFrameSize }
  /** The playthrough is no longer wanted (the pointer or focus left the card). */
  | { type: 'stop'; at: number }
  /** A frame's document finished loading. */
  | { type: 'loaded'; key: number; at: number }
  /** Drop the frames whose fade has finished (covered by a newer frame of their mode, or faded out). */
  | { type: 'settle'; at: number; fadeMs: number };

export const EMPTY_DEMO_STACK: DemoStack = { layers: [], nextKey: 1 };

/** The frame is on screen (or fading in): loaded and still wanted. Pure. */
export const demoLayerShown = (layer: DemoLayer): boolean => layer.shownAt !== null && layer.leftAt === null;

/** The newest shown frame of the layer's mode above it, if any. */
const coverOf = (layer: DemoLayer, layers: readonly DemoLayer[]): DemoLayer | undefined =>
  layers.filter((o) => o.mode === layer.mode && o.key > layer.key && demoLayerShown(o)).pop();

/** When the layer can be dropped (ms), or null while it is needed. */
const dueAt = (layer: DemoLayer, layers: readonly DemoLayer[], fadeMs: number): number | null => {
  if (layer.leftAt !== null) return layer.leftAt + fadeMs;
  const cover = coverOf(layer, layers);
  return cover?.shownAt != null ? cover.shownAt + fadeMs : null;
};

/** The stack after an event. Pure: an event that changes nothing returns the same stack. */
export const demoStackReducer = (stack: DemoStack, ev: DemoEvent): DemoStack => {
  switch (ev.type) {
    case 'mount': {
      // An older frame of the same mode that is still loading was never seen: the new one replaces it.
      const kept = stack.layers.filter((l) => l.mode !== ev.mode || l.shownAt !== null);
      const layer: DemoLayer = { key: stack.nextKey, mode: ev.mode, html: ev.html, shownAt: null, leftAt: null };
      if (ev.size) layer.size = ev.size;
      return { layers: [...kept, layer], nextKey: stack.nextKey + 1 };
    }
    case 'stop': {
      if (!stack.layers.some((l) => l.mode === 'play' && l.leftAt === null)) return stack;
      const layers = stack.layers
        .filter((l) => l.mode !== 'play' || l.shownAt !== null)
        .map((l) => (l.mode === 'play' && l.leftAt === null ? { ...l, leftAt: ev.at } : l));
      return { ...stack, layers };
    }
    case 'loaded': {
      if (!stack.layers.some((l) => l.key === ev.key && l.shownAt === null)) return stack;
      return { ...stack, layers: stack.layers.map((l) => (l.key === ev.key ? { ...l, shownAt: ev.at } : l)) };
    }
    case 'settle': {
      const layers = stack.layers.filter((l) => {
        const due = dueAt(l, stack.layers, ev.fadeMs);
        return due === null || due > ev.at;
      });
      return layers.length === stack.layers.length ? stack : { ...stack, layers };
    }
    default:
      return stack;
  }
};

/** How long until the next frame can be dropped (ms, 0 when one is already due), or null when none is waiting. Pure. */
export const demoSettleDelay = (stack: DemoStack, at: number, fadeMs: number): number | null => {
  const dues = stack.layers.map((l) => dueAt(l, stack.layers, fadeMs)).filter((d): d is number => d !== null);
  return dues.length === 0 ? null : Math.max(0, Math.min(...dues) - at);
};
